import React, { useEffect, useRef, useState } from 'react';
import type { AnyMessage } from './chatTypes';
import { VBox } from '../layout';
import ChatMessageComponent from './ChatMessageComponents/ChatMessageComponent';

export interface ChatWindowProps {
  messages: AnyMessage[];
  chatInputComponent: React.JSX.Element;
  emptyStatePanel?: React.JSX.Element;
  className?: string;
}

export function ChatWindow({
  messages,
  chatInputComponent,
  emptyStatePanel,
  className = '',
  ...props
}: Readonly<ChatWindowProps>): React.JSX.Element {
  const scrollRef = useRef<HTMLDivElement>(null);
  const messageRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [activeMessageIndex, setActiveMessageIndex] = useState<number>(() =>
    messages.length > 0 ? messages.length - 1 : -1
  );

  useEffect(() => {
    scrollRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  }, [scrollRef, messages]);

  useEffect(() => {
    if (messages.length === 0) {
      setActiveMessageIndex(-1);
      return;
    }

    if (activeMessageIndex < 0 || activeMessageIndex >= messages.length) {
      setActiveMessageIndex(messages.length - 1);
    }
  }, [activeMessageIndex, messages.length]);

  useEffect(() => {
    if (activeMessageIndex < 0) {
      return;
    }

    messageRefs.current[activeMessageIndex]?.focus();
  }, [activeMessageIndex]);

  const focusMessage = (index: number) => {
    if (index < 0 || index >= messages.length) {
      return;
    }

    setActiveMessageIndex(index);
    messageRefs.current[index]?.focus();
  };

  return (
    <VBox {...props} className={`ehs-chat-window ${className}`.trim()}>
      <VBox
        className={
          messages.length === 0
            ? 'no-messages chat-window-body'
            : 'chat-window-body'
        }
      >
        {messages.length
          ? messages.map((message, index) => (
              <ChatMessageComponent
                key={
                  message.id ??
                  message.txnId ??
                  `${message.date.getTime()}-${message.user.uid}`
                }
                message={message}
                tabIndex={index === activeMessageIndex ? 0 : -1}
                messageRef={element => {
                  messageRefs.current[index] = element;
                }}
                onFocus={() => setActiveMessageIndex(index)}
                onKeyDown={event => {
                  if (event.key === 'ArrowUp') {
                    event.preventDefault();
                    focusMessage(index - 1);
                  }

                  if (event.key === 'ArrowDown') {
                    event.preventDefault();
                    focusMessage(index + 1);
                  }
                }}
              />
            ))
          : emptyStatePanel}
        <div className="scroll-div" ref={scrollRef} />
      </VBox>
      <div className="chat-window-input">{chatInputComponent}</div>
    </VBox>
  );
}
